import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ConfigService } from '../services/config.service';
import { BehaviorSubject, Observable, OperatorFunction, defer, firstValueFrom, from, map, retry, throwError, timeout } from 'rxjs';
import ResultClass from '../model/api/resultClass';
import LoginService from './login/loginService';
import UserModel from '../model/api/generic/userModel';
import InspeccionesService from './inspecciones/inspeccionesService';
import DatosService from './datos/datosService';
import CertificadosService from './certificados/certificadosService';
import VersionesService from './versiones/versiones';
import { GlobalesService } from '../services/globales.service';
import OtpService from './otp/otpService';

@Injectable({
  providedIn: 'root'
})
export class MainService {

  //#region Servicios

  public Login = new LoginService(this)
  public Inspecciones = new InspeccionesService(this)
  public Datos = new DatosService(this)
  public Certificados = new CertificadosService(this)
  public Versiones = new VersionesService(this)
  public Otp = new OtpService(this)

  //#endregion

  //#region Propiedades

  public URL: string
  private _loginToken: string
  public get loginToken() {
    return this._loginToken
  }
  public set loginToken(value: string) {
    this._loginToken = value
    this.GetCurrentUser().then(user => {
      if (user) {
        this.userName.next(user.UserName)
        this.NombreCompleto.next(user.NombreCompleto)
      }
    })
  }
  public userName = new BehaviorSubject<string>("")
  public NombreCompleto = new BehaviorSubject<string>("")
  public inspeccionToken: string

  //#endregion

  //#region Constructor

  constructor(
    private config: ConfigService,
    private http: HttpClient,
    private ngZone: NgZone,
    private globales: GlobalesService
  ) {
    this.URL = this.config.urlService
    if (sessionStorage.getItem('token') != null) {
      this.loginToken = sessionStorage.getItem('token')
    } else {
      if (localStorage.getItem('token') != null) {
        this.loginToken = localStorage.getItem('token')
      }
    }
  }

  //#endregion

  //#region Autenticación

  public async Reauthenticate(): Promise<void> {
    try {
      let token: string = await firstValueFrom(this.ReLogin)
      this.loginToken = token
    } catch (ex) {
      console.error(ex)
      throw ex
    }
  }

  //#endregion

  //#region Configuración de la petición

  private ConfigureHeaders(contentType?: string): HttpHeaders {
    if (contentType == null) contentType = 'application/json'
    let headers = new HttpHeaders()
    if (contentType as any !== false) {
      headers = headers.append('Content-Type', contentType)
    }
    if (this.loginToken != null) {
      headers = headers.append('Authorization', `bearer ${this.loginToken}`)
    }
    headers = headers.append('CopiaDeSeguridadActiva', JSON.stringify(this.globales.backupConfigurado.value === 1))
    headers = headers.append('AppVersion', this.globales.version)
    return headers
  }

  private ConfigureParams(params: any): HttpParams {
    params = params || {}
    let httpParams = new HttpParams()
    for (let param in params) {
      if (params[param] != null) {
        httpParams = httpParams.append(param, params[param]?.toString() || '')
      } else {
        httpParams = httpParams.append(param, '')
      }
    }
    return httpParams
  }

  //#endregion

  //#region Pipes

  public ResultClassPipe<T>(): OperatorFunction<any, any> {
    return map((result: ResultClass<T>) => {
      if (result.Error && result.Error.HasError) {
        throw new Error(result.Error.Message)
      }
      return result.content
    })
  }

  public HandleHttpErrorPipe<T>(useResultClass: boolean): OperatorFunction<any, any> {
    if (useResultClass == null) useResultClass = true
    return retry({
      delay: (error, retryCount) => {
        if (retryCount < 2) {

          // Si es un 401 y es el primer reintento (retryCount < 2) reautenticamos
          if (error?.status == 401) {
            return defer(async () => await this.Reauthenticate())
          }

        }
        if (useResultClass) {
          let result: ResultClass<T> = error?.error
          if (result?.Error?.Message != null) {
            return throwError(() => new Error(result?.Error?.Message || 'Error desconocido.'))
          }
        }
        return throwError(() => error)
      }
    })
  }

  public HandleResultClassErrorSimple<T>(): OperatorFunction<any, any> {
    return retry({
      delay: (error) => {
        let result: ResultClass<T> = error?.error || 'Error desconocido.'
        if (result?.Error?.Message != null) {
          return throwError(() => new Error(result?.Error?.Message || 'Error desconocido.'))
        } else {
          return throwError(() => error)
        }
      }
    })
  }

  //#endregion

  //#region Funciones HTTP

  public HttpGet<T>(url: string, params?: any, useResultClass?: boolean, useReauthenticate?: boolean): Promise<T> {
    let pipes: OperatorFunction<any, any>[] = []
    if (useResultClass == null || useResultClass == true) {
      pipes.push(this.ResultClassPipe<T>())
    }
    if (useReauthenticate == null || useReauthenticate == true) {
      pipes.push(this.HandleHttpErrorPipe<T>(useResultClass))
    } else if (useResultClass) {
      pipes.push(this.HandleResultClassErrorSimple<T>())
    }
    return firstValueFrom(this.HttpGetObservable(this.URL + url, {
      headers: this.ConfigureHeaders(),
      params: this.ConfigureParams(params),
      withCredentials: true
    }).pipe(...pipes as [OperatorFunction<any, any>]))
  }

  public HttpPost<T>(url: string, params?: any, useResultClass?: boolean, useReauthenticate?: boolean): Promise<T> {
    let pipes: OperatorFunction<any, any>[] = []
    if (useResultClass == null || useResultClass == true) {
      pipes.push(this.ResultClassPipe<T>())
    }
    if (useReauthenticate == null || useReauthenticate == true) {
      pipes.push(this.HandleHttpErrorPipe<T>(useResultClass))
    } else if (useResultClass) {
      pipes.push(this.HandleResultClassErrorSimple<T>())
    }
    params = params || {}
    return firstValueFrom(this.HttpPostObservable(this.URL + url, JSON.stringify(params), {
      headers: this.ConfigureHeaders(),
      withCredentials: true
    }).pipe(...pipes as [OperatorFunction<any, any>]))
  }

  public HttpPostFormData<T>(url: string, formData: FormData, useResultClass?: boolean, useReauthenticate?: boolean): Promise<T> {
    let pipes: OperatorFunction<any, any>[] = []
    if (useResultClass == null || useResultClass == true) {
      pipes.push(this.ResultClassPipe<T>())
    }
    if (useReauthenticate == null || useReauthenticate == true) {
      pipes.push(this.HandleHttpErrorPipe<T>(useResultClass))
    } else if (useResultClass) {
      pipes.push(this.HandleResultClassErrorSimple<T>())
    }
    return firstValueFrom(this.HttpPostObservable(this.URL + url, formData, {
      headers: this.ConfigureHeaders(false as any),
      withCredentials: true
    }).pipe(...pipes as [OperatorFunction<any, any>]))
  }

  public HttpPut<T>(url: string, params?: any, useResultClass?: boolean, useReauthenticate?: boolean): Promise<T> {
    let pipes: OperatorFunction<any, any>[] = []
    if (useResultClass == null || useResultClass == true) {
      pipes.push(this.ResultClassPipe<T>())
    }
    if (useReauthenticate == null || useReauthenticate == true) {
      pipes.push(this.HandleHttpErrorPipe<T>(useResultClass))
    } else if (useResultClass) {
      pipes.push(this.HandleResultClassErrorSimple<T>())
    }
    params = params || {}
    return firstValueFrom(this.HttpPutObservable(this.URL + url, JSON.stringify(params), {
      headers: this.ConfigureHeaders(),
      withCredentials: true
    }).pipe(...pipes as [OperatorFunction<any, any>]))
  }

  public HttpPutFormData<T>(url: string, formData: FormData, useResultClass?: boolean, useReauthenticate?: boolean): Promise<T> {
    let pipes: OperatorFunction<any, any>[] = []
    if (useResultClass == null || useResultClass == true) {
      pipes.push(this.ResultClassPipe<T>())
    }
    if (useReauthenticate == null || useReauthenticate == true) {
      pipes.push(this.HandleHttpErrorPipe<T>(useResultClass))
    } else if (useResultClass) {
      pipes.push(this.HandleResultClassErrorSimple<T>())
    }
    return firstValueFrom(this.HttpPutObservable(this.URL + url, formData, {
      headers: this.ConfigureHeaders(false as any),
      withCredentials: true
    }).pipe(...pipes as [OperatorFunction<any, any>]))
  }

  public HttpDelete<T>(url: string, params?: any, useResultClass?: boolean, useReauthenticate?: boolean): Promise<T> {
    let pipes: OperatorFunction<any, any>[] = []
    if (useResultClass == null || useResultClass == true) {
      pipes.push(this.ResultClassPipe<T>())
    }
    if (useReauthenticate == null || useReauthenticate == true) {
      pipes.push(this.HandleHttpErrorPipe<T>(useResultClass))
    } else if (useResultClass) {
      pipes.push(this.HandleResultClassErrorSimple<T>())
    }
    return firstValueFrom(this.HttpDeleteObservable(this.URL + url, {
      headers: this.ConfigureHeaders(),
      params: this.ConfigureParams(params),
      withCredentials: true
    }).pipe(...pipes as [OperatorFunction<any, any>]))
  }

  public async HttpGetDownloadBase64(url: string, params?: any, requestTimeout?: number): Promise<string> {
    params = params || {}
    requestTimeout = requestTimeout || 30000
    try {
      const headers: Record<string, string> = {}
      if (this.loginToken) {
        headers["Authorization"] = `Bearer ${this.loginToken}`
      }
      if (this.inspeccionToken) {
        headers["InspeccionToken"] = this.inspeccionToken
      }
      const query = new URLSearchParams()
      for (let name in params) {
        query.append(name, params[name]?.toString() || '')
      }
      const response: Response = await fetch(`${this.URL}${url}${query.size ? `?${query.toString()}` : ''}`, {
        headers: headers
      })
      if (response.ok) {
        const blob: Blob = await response.blob()
        const base64String: string = await firstValueFrom(from(new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })).pipe(
          timeout(requestTimeout)
        ))
        return base64String
      } else {
        throw new Error(`La solicitud para descargar el fichero falló con código ${response.status}`)
      }
    } catch (ex) {
      throw new Error(`Error al descargar el fichero: ${ex}`)
    }
  }

  //#endregion

  //#region Funciones Custom HTTP

  /* Utilizaremos estas funciones para procesar el Token cada vez que se disparan, de esa forma el Retry de HandleHttpErrorPipe intentará la petición con un token nuevo y no
     con el mismo token que se creó el observable original. */

  private HttpGetObservable(url: string, params: {
    headers: HttpHeaders,
    params: HttpParams,
    withCredentials: boolean
  }): Observable<any> {
    return defer(() => {
      if (this.loginToken != null) {
        params.headers = params.headers.delete('Authorization')
        params.headers = params.headers.append('Authorization', `bearer ${this.loginToken}`)
      }
      if (this.inspeccionToken != null) {
        params.headers = params.headers.delete('InspeccionToken')
        params.headers = params.headers.append('InspeccionToken', this.inspeccionToken)
      }
      return this.http.get(url, {
        headers: params.headers,
        params: params.params,
        withCredentials: params.withCredentials
      })
    })
  }

  private HttpPostObservable(url: string, body: string | FormData, params: {
    headers: HttpHeaders,
    withCredentials: boolean
  }): Observable<any> {
    return defer(() => {
      if (this.loginToken != null) {
        params.headers = params.headers.delete('Authorization')
        params.headers = params.headers.append('Authorization', `bearer ${this.loginToken}`)
      }
      if (this.inspeccionToken != null) {
        params.headers = params.headers.delete('InspeccionToken')
        params.headers = params.headers.append('InspeccionToken', this.inspeccionToken)
      }
      return this.http.post(url, body, {
        headers: params.headers,
        withCredentials: params.withCredentials
      })
    })
  }

  private HttpPutObservable(url: string, body: string | FormData, params: {
    headers: HttpHeaders,
    withCredentials: boolean
  }): Observable<any> {
    return defer(() => {
      if (this.loginToken != null) {
        params.headers = params.headers.delete('Authorization')
        params.headers = params.headers.append('Authorization', `bearer ${this.loginToken}`)
      }
      if (this.inspeccionToken != null) {
        params.headers = params.headers.delete('InspeccionToken')
        params.headers = params.headers.append('InspeccionToken', this.inspeccionToken)
      }
      return this.http.put(url, body, {
        headers: params.headers,
        withCredentials: params.withCredentials
      })
    })
  }

  private HttpDeleteObservable(url: string, params: {
    headers: HttpHeaders,
    params: HttpParams,
    withCredentials: boolean
  }): Observable<any> {
    return defer(() => {
      if (this.loginToken != null) {
        params.headers = params.headers.delete('Authorization')
        params.headers = params.headers.append('Authorization', `bearer ${this.loginToken}`)
      }
      if (this.inspeccionToken != null) {
        params.headers = params.headers.delete('InspeccionToken')
        params.headers = params.headers.append('InspeccionToken', this.inspeccionToken)
      }
      return this.http.delete(url, {
        headers: params.headers,
        params: params.params,
        withCredentials: params.withCredentials
      })
    })
  }

  //#endregion
  
  //#region Usuario

  // Esta función es una promesa porque se utiliza desde un observable en auth.guard.
  // de esta forma en ciertas circustancias podríamos hacer peticiones a servicio.
  public async GetCurrentUser(): Promise<UserModel> {
    if (this.loginToken == null) return null
    let tokenData = JSON.parse(atob(this.loginToken.split(".")[1]))

    let userModel = new UserModel()
    userModel.Token = this.loginToken
    userModel.UserName = tokenData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"]
    userModel.NombreCompleto = tokenData.NombreCompleto

    return userModel
  }

  // Función que nos devuelve si el usuario tiene el modo Debug habilitado
  public IsDebugUser(): boolean {
    if (this.loginToken == null) return false
    let tokenData = JSON.parse(atob(this.loginToken.split(".")[1]))

    return tokenData?.DebugUser === "true"
  }

  //#endregion

  //#region Relogin

  // Funcionalidad para que cuando una petición de un 401 (No autenticado)
  // aparezca la ventana que vuelve a solicitar el login y a continuación resuelva
  // un observable para que la petición continue.
  private resolveLoginCallbacks: ((token: string) => void)[] = []
  public rejectLoginCallbacks: ((reason?: any) => void)[] = []
  public showLogin = new BehaviorSubject<boolean>(false)

  public ReLogin = defer(() => {
    return new Promise<string>((resolve, reject) => {
      if (this.resolveLoginCallbacks.length < 1) {
        // Mostramos diálogo de Login.
        this.ngZone.run(() => {
          this.showLogin.next(true)
        })
      }
      this.resolveLoginCallbacks.push(resolve)
      this.rejectLoginCallbacks.push(reject)
    })
  })

  public onLoggedIn(): void {
    this.showLogin.next(false)
    this.resolveLoginCallbacks.forEach(p => p(this.loginToken))
    this.resolveLoginCallbacks = []
    this.rejectLoginCallbacks = []
  }

  public onCancelLogin(): void {
    this.showLogin.next(false)
    this.rejectLoginCallbacks.forEach(p => p(new Error('No autenticado.')))
    this.resolveLoginCallbacks = []
    this.rejectLoginCallbacks = []
  }

  //#endregion

}
